import { useMutation, useQuery } from '@tanstack/react-query';
import { useParams, useRouteContext } from '@tanstack/react-router';
import { useState } from 'react';
import { Button, EmptyState } from '@bb/ui';
import { ApiError, type SegmentInsights } from '../../api/client.ts';

const MAX = 5;
type Kpis = SegmentInsights['baseline'];
const KPIS: Array<{ key: Exclude<keyof Kpis, 'profiles'>; label: string }> = [
  { key: 'sessionsPerDay', label: 'Sessions per day (30 days)' },
  { key: 'daysSinceFirstSession', label: 'Days since first session' },
  { key: 'daysSinceLastSession', label: 'Days since last session' },
];
const show = (v: number | null) => (v === null ? '—' : v.toLocaleString('en-US', { maximumFractionDigits: 2 }));

/** Audience → Segment insights (BB-48): session KPIs of up to five segments against every active user. */
export function SegmentInsightsPage() {
  const { environmentId } = useParams({ from: '/env/$environmentId/audience/segment-insights' });
  const { api } = useRouteContext({ from: '/env/$environmentId/audience/segment-insights' });
  const segments = useQuery({ queryKey: ['segments', environmentId, { status: 'active' }, 'insights'], queryFn: () => api.listSegments(environmentId, { status: 'active' }) });
  const [chosen, setChosen] = useState<string[]>([]);
  const insights = useMutation({ mutationFn: () => api.segmentInsights(environmentId, chosen) });
  const result = insights.data;
  const columns = result ? [{ name: 'All users', kpis: result.baseline }, ...result.segments.map((s) => ({ name: s.name, kpis: s.kpis }))] : [];
  return (
    <section>
      <h1>Segment insights</h1>
      <p className="hint">Compare how often users of up to {MAX} segments use the apps with every active user.</p>
      {(segments.data?.items ?? []).length === 0 ? (
        <EmptyState title="No segments" description="Create a segment to compare it." />
      ) : (
        <fieldset aria-label="Segments to compare">
          <legend>Segments ({chosen.length} of {MAX})</legend>
          {segments.data!.items.map((s) => (
            <label key={s.id}>
              <input type="checkbox" checked={chosen.includes(s.id)} disabled={!chosen.includes(s.id) && chosen.length >= MAX} onChange={(e) => setChosen(e.target.checked ? [...chosen, s.id] : chosen.filter((x) => x !== s.id))} /> {s.name}
            </label>
          ))}
        </fieldset>
      )}
      <Button onClick={() => insights.mutate()} disabled={chosen.length === 0 || insights.isPending}>
        Compare
      </Button>
      {insights.isError && <p role="alert">{insights.error instanceof ApiError ? insights.error.message : 'The comparison failed.'}</p>}
      {result && (
        <>
          <table className="table" aria-label="Session KPIs">
            <thead>
              <tr>
                <th scope="col">KPI</th>
                {columns.map((c) => (
                  <th scope="col" key={c.name}>
                    {c.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row">Users</th>
                {columns.map((c) => (
                  <td key={c.name}>{c.kpis.profiles.toLocaleString('en-US')}</td>
                ))}
              </tr>
              {KPIS.map((k) => (
                <tr key={k.key}>
                  <th scope="row">{k.label}</th>
                  {columns.map((c) => (
                    <td key={c.name}>
                      {show(c.kpis[k.key].median)} <span className="hint">(mean {show(c.kpis[k.key].mean)})</span>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="hint">{result.sampled ? 'From a stable 10% sample of users.' : 'Counted over every user.'} Medians; means in brackets.</p>
        </>
      )}
    </section>
  );
}
